import { Sparkles } from "lucide-react";

/**
 * Skim-friendly card placed near the top of every proof case study page.
 * Buyers who don't read deep get the full picture in 15 seconds; the long
 * narrative stays below the fold for the deep readers.
 *
 * Props:
 *   - testid:   data-testid prefix for the wrapper
 *   - problem:  one-line description of the client situation
 *   - approach: one-line description of what SideB did
 *   - outcome:  one-line value-led result statement
 */
export function ProofTldr({ testid, problem, approach, outcome }) {
  return (
    <section
      className="max-w-3xl mx-auto px-6 lg:px-10 mt-14 lg:mt-16"
      data-testid={testid}
    >
      <div className="card-sharp p-6 lg:p-7 border-[#ac62ea]/35 bg-gradient-to-br from-[#f3eefb]/60 via-white to-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#ac62ea]" />
          <span className="label-mono text-[#7d3fbf]">If you only read this</span>
        </div>
        <dl className="mt-4 space-y-4 sm:space-y-3 text-foreground/85 leading-relaxed">
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-foreground/55 sm:w-20 sm:flex-shrink-0 sm:pt-0.5">Problem</dt>
            <dd className="text-sm lg:text-base min-w-0 break-words">{problem}</dd>
          </div>
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-foreground/55 sm:w-20 sm:flex-shrink-0 sm:pt-0.5">Approach</dt>
            <dd className="text-sm lg:text-base min-w-0 break-words">{approach}</dd>
          </div>
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-[#ac62ea] sm:w-20 sm:flex-shrink-0 sm:pt-0.5">Outcome</dt>
            <dd className="text-sm lg:text-base font-medium text-foreground min-w-0 break-words">{outcome}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

/**
 * Collapsed-by-default methodology disclosure. Keeps the math available for
 * scrutiny without weighing down the page.
 */
export function MethodologyNote({ children, testid = "proof-methodology" }) {
  return (
    <details
      className="mt-6 group rounded-xl border border-white/15 bg-white/[0.04] open:bg-white/[0.06] transition-colors"
      data-testid={testid}
    >
      <summary className="cursor-pointer list-none p-4 flex items-center justify-between gap-3 text-white/80 hover:text-white text-xs uppercase tracking-widest font-semibold">
        <span>How we measured this</span>
        <span className="text-white/60 group-open:rotate-180 transition-transform" aria-hidden>▾</span>
      </summary>
      <div className="px-4 pb-4 text-white/70 text-xs leading-relaxed">
        {children}
      </div>
    </details>
  );
}
